import React from 'react';
import Avatar from '@atlaskit/avatar';
import DynamicTable from '@atlaskit/dynamic-table';
import EditorPanelIcon from '@atlaskit/icon/core/migration/information--editor-panel';
import { Box, Inline, Text, xcss } from '@atlaskit/primitives';
import Tooltip from '@atlaskit/tooltip';
import { FormattedMessage } from '@atlassian/jira-intl';
import {
	MST_IMAGE_URL,
	MST_TEXT,
	SLACK_IMAGE_URL,
	SLACK_TEXT,
} from '../../../../common/constants/index.tsx';
import type { ChatToolsState } from '../../../../services/store/chat-tools/types.tsx';
import { Action } from './action/index.tsx';
import { messages } from './messages.tsx';

export const ConnectChatToolsTable = ({
	connections,
}: {
	connections: ChatToolsState['connections'];
}) => {
	if (!connections.data) {
		throw new Error(
			'ConnectChatToolsTable should be rendered when chat tools connections are loaded',
		);
	}

	const rows = connections.data.values.map((item, index) => ({
		key: `row-${index}`,
		cells: [
			{
				key: `cell-${index}-workspace`,
				content: (
					<Box paddingInlineStart="space.100">
						<Text as="p" maxLines={1}>
							{item.chatSpaceName}
						</Text>
					</Box>
				),
			},
			{
				key: `cell-${index}-product`,
				content: (
					<Inline space="space.100" alignBlock="center">
						<Avatar
							size="xsmall"
							src={item.chatSpaceType === 'slack' ? SLACK_IMAGE_URL : MST_IMAGE_URL}
						/>
						<Text as="p" maxLines={1}>
							{item.chatSpaceType === 'slack' ? SLACK_TEXT : MST_TEXT}
						</Text>
					</Inline>
				),
			},
			{
				key: `cell-${index}-status`,
				content: (
					<Inline space="space.050" alignBlock="center" alignInline="center" xcss={inlineStyles}>
						<Action item={item} />
					</Inline>
				),
			},
		],
	}));

	const head = {
		cells: [
			{
				key: 'workspace-or-tenant',
				content: (
					<Box paddingInlineStart="space.100">
						<FormattedMessage {...messages.chatToolsConnectTableWorkspaceHeadNonFinal} />
					</Box>
				),
				isSortable: true,
			},
			{
				key: 'product',
				content: <FormattedMessage {...messages.chatToolsConnectTableProductHeadNonFinal} />,
			},
			{
				key: 'status',
				content: (
					<Inline space="space.050" alignInline="center" alignBlock="center">
						<FormattedMessage {...messages.chatToolsConnectTableStatusHeadNonFinal} />

						<Tooltip
							position="top"
							content={
								<FormattedMessage {...messages.chatToolsConnectTableStatusHeadTooltipNonFinal} />
							}
						>
							<Box xcss={infoIconBoxStyles}>
								<EditorPanelIcon LEGACY_size="small" label="" />
							</Box>
						</Tooltip>
					</Inline>
				),
			},
		],
	};

	return (
		<DynamicTable
			isLoading={connections.loading}
			isFixedSize
			head={head}
			rows={rows}
			rowsPerPage={5}
			defaultPage={1}
		/>
	);
};

const infoIconBoxStyles = xcss({
	height: 'size.100',
});

const inlineStyles = xcss({
	height: 'size.400',
	cursor: 'default',
});
